import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Sailboat } from "lucide-react";
import { api } from "../lib/api";
import { ActivityCard } from "../components/Cards";
import { Pagination } from "../components/Pagination";
import { useTerms } from "../lib/useTerms";
import { useSeo } from "../components/VIP";

const inp = "bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-rose-500 text-sm";
const STATES = ["", "Florida", "Alabama", "Mississippi", "Louisiana", "Texas"];

export default function Activities() {
  useSeo("Activities & Tours | Gulf Coast VIP", "Book Gulf Coast experiences — sunset cruises, dolphin tours, fishing charters, rentals, classes and more.");
  const [params] = useSearchParams();
  const [data, setData] = useState({ items: [], pages: 1, total: 0 });
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState(params.get("type") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [state, setState] = useState(params.get("state") || "");
  const [family, setFamily] = useState(false);
  const [difficulty, setDifficulty] = useState("");
  const [duration, setDuration] = useState("");
  const [page, setPage] = useState(1);
  const types = useTerms("activity", "activity_type", []);
  const difficulties = useTerms("activity", "difficulty", ["Easy", "Moderate", "Challenging", "All Skill Levels"]);
  const durations = useTerms("activity", "duration", ["Under 1 hour", "1-2 hours", "2-4 hours", "Half day", "Full day"]);

  useEffect(() => {
    const p = new URLSearchParams({ page, limit: 12 });
    if (q) p.set("q", q);
    if (type) p.set("activity_type", type);
    if (city) p.set("city", city);
    if (state) p.set("state", state);
    if (difficulty) p.set("difficulty", difficulty);
    if (duration) p.set("duration", duration);
    if (family) p.set("family_friendly", "true");
    const t = setTimeout(() => api.get(`/activities?${p}`).then((r) => setData(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, type, city, state, family, difficulty, duration, page]);

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="activities-page">
      <p className="overline mb-2">Experiences & tours</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-8 flex items-center gap-3"><Sailboat className="text-rose-400" size={36} /> Activities</h1>
      <div className="flex flex-wrap gap-3 mb-8">
        <div className={`${inp} flex items-center gap-2 flex-1 min-w-[200px]`}>
          <Search size={15} className="text-zinc-500" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search activities..." className="bg-transparent outline-none w-full" data-testid="activities-search" />
        </div>
        <select className={inp} value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} data-testid="activities-type-filter">
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className={`${inp} flex items-center gap-2`}>
          <MapPin size={15} className="text-zinc-500" />
          <input value={city} onChange={(e) => { setCity(e.target.value); setPage(1); }} placeholder="City" className="bg-transparent outline-none w-28" data-testid="activities-city-filter" />
        </div>
        <select className={inp} value={state} onChange={(e) => { setState(e.target.value); setPage(1); }} data-testid="activities-state-filter">
          {STATES.map((s) => <option key={s} value={s}>{s || "All states"}</option>)}
        </select>
        <select className={inp} value={difficulty} onChange={(e) => { setDifficulty(e.target.value); setPage(1); }} data-testid="activities-difficulty-filter">
          <option value="">Skill level</option>
          {difficulties.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inp} value={duration} onChange={(e) => { setDuration(e.target.value); setPage(1); }} data-testid="activities-duration-filter">
          <option value="">Duration</option>
          {durations.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className={`${inp} flex items-center gap-2 cursor-pointer`}>
          <input type="checkbox" checked={family} onChange={(e) => { setFamily(e.target.checked); setPage(1); }} className="accent-rose-500" data-testid="activities-family-filter" /> Family friendly
        </label>
      </div>
      <p className="text-sm text-zinc-500 mb-5">{data.total} activit{data.total === 1 ? "y" : "ies"}</p>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {data.items.map((a) => <ActivityCard key={a.id} a={a} />)}
      </div>
      {data.items.length === 0 && <p className="text-zinc-500 text-center py-16">No activities found — try different filters.</p>}
      <div className="mt-10"><Pagination page={page} pages={data.pages} onPage={setPage} /></div>
    </div>
  );
}
